import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  useToast,
  Flex,
  Spacer,
  Select,
  VStack,
  Text,
  Center,
  Badge,
  Input,
  HStack,
  IconButton,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  useDisclosure,
  Checkbox,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Stack,
  Stat,
  StatLabel,
  StatNumber,
  StatGroup,
  useBreakpointValue,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import rbacService from "../../../services/rbacService.js";

// RBAC page component
const RBAC = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState({});
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isModulesOpen,
    onOpen: onModulesOpen,
    onClose: onModulesClose,
  } = useDisclosure();
  const cancelRef = useRef();
  const [moduleOptions, setModuleOptions] = useState([]);
  const [selectedModules, setSelectedModules] = useState([]);

  const isMobile = useBreakpointValue({ base: true, md: false });

  const totalUsers = users.length;
  const activeUsers = users.filter(
    (u) => (u.status || "Active") === "Active"
  ).length;
  const rolesCount = Object.keys(roles || {}).length;
  const modulesCount = moduleOptions.length;

  const load = async () => {
    setLoading(true);
    try {
      const [u, r] = await Promise.all([
        rbacService.getAllUsers(),
        rbacService.getRoleDefinitions(),
      ]);
      setUsers(u || []);
      setRoles(r || {});
      // fetch module options too
      try {
        const mods = await rbacService.getModuleOptions();
        setModuleOptions(mods || []);
      } catch (e) {
        console.error("failed to load module options", e);
      }
    } catch (e) {
      console.error(e);
      toast({ title: "Failed to load RBAC data", status: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = users?.filter((u) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (
      (u.name || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q) ||
      (u.employeeId || "").toLowerCase().includes(q)
    );
  });

  const openConfirm = (user) => {
    setSelectedUser(user);
    onOpen();
  };

  const updateRole = async (user, roleKey) => {
    try {
      await rbacService.updateUserRole(user._id, { role: roleKey });
      toast({ title: "Role updated", status: "success" });
      load();
    } catch (e) {
      console.error(e);
      toast({
        title: e?.response?.data?.message || "Update failed",
        status: "error",
      });
    }
  };

  return (
    <Box p={4}>
      <Flex align="center" mb={4}>
        <Heading size="md">RBAC — Users & Roles</Heading>
        <Spacer />
        <HStack spacing={2}>
          <Input
            placeholder="Search by name, email, employee id"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            size={isMobile ? "sm" : "md"}
            width={isMobile ? "160px" : "320px"}
          />
          <IconButton
            aria-label="Search"
            icon={<SearchIcon />}
            onClick={load}
          />
        </HStack>
      </Flex>

      {/* KPI cards */}
      <Box mb={4}>
        <StatGroup>
          <HStack spacing={3}>
            <Box p={3} borderWidth={1} borderRadius="md" minW="140px">
              <Stat>
                <StatLabel>Total Users</StatLabel>
                <StatNumber>{totalUsers}</StatNumber>
              </Stat>
            </Box>
            <Box p={3} borderWidth={1} borderRadius="md" minW="140px">
              <Stat>
                <StatLabel>Active Users</StatLabel>
                <StatNumber>{activeUsers}</StatNumber>
              </Stat>
            </Box>
            <Box p={3} borderWidth={1} borderRadius="md" minW="140px">
              <Stat>
                <StatLabel>Roles</StatLabel>
                <StatNumber>{rolesCount}</StatNumber>
              </Stat>
            </Box>
            <Box p={3} borderWidth={1} borderRadius="md" minW="140px">
              <Stat>
                <StatLabel>Modules</StatLabel>
                <StatNumber>{modulesCount}</StatNumber>
              </Stat>
            </Box>
          </HStack>
        </StatGroup>
      </Box>

      <Box borderWidth={1} p={3} borderRadius="md">
        {loading ? (
          <Center p={6}>
            <Text>Loading...</Text>
          </Center>
        ) : isMobile ? (
          <VStack spacing={3} align="stretch">
            {filtered.map((u) => {
              const primary = u.role || (u.roles && u.roles[0]) || "";
              return (
                <Box key={u._id} p={3} borderWidth={1} borderRadius="md">
                  <Flex direction="column">
                    <HStack justify="space-between" align="start">
                      <Box>
                        <Text fontWeight="bold">{u.name || u.email}</Text>
                        <Text fontSize="sm" color="gray.600">
                          {u.email}
                        </Text>
                      </Box>
                      <HStack spacing={1}>
                        {(u.roles || (primary ? [primary] : [])).map((r) => {
                          const rd = roles[r] || {};
                          return (
                            <Badge key={r} colorScheme={rd.color || "gray"}>
                              {rd.name || r}
                            </Badge>
                          );
                        })}
                      </HStack>
                    </HStack>

                    <Text mt={2} fontSize="sm">
                      {u.employeeId ? `Employee ID: ${u.employeeId}` : ""}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      {u.status || "Active"} •{" "}
                      {new Date(u.updatedAt || Date.now()).toLocaleDateString()}
                    </Text>

                    <Stack mt={3} spacing={2}>
                      <Select
                        size="sm"
                        value={primary}
                        onChange={(e) => updateRole(u, e.target.value)}
                      >
                        <option value="">(none)</option>
                        {Object.keys(roles).map((k) => (
                          <option key={k} value={k}>
                            {roles[k].name || k}
                          </option>
                        ))}
                      </Select>

                      <HStack>
                        <Button
                          flex={1}
                          size="sm"
                          onClick={() => openConfirm(u)}
                        >
                          Confirm
                        </Button>
                        <Button
                          flex={1}
                          size="sm"
                          onClick={() => {
                            setSelectedUser(u);
                            setSelectedModules(u.allowed_modules || []);
                            onModulesOpen();
                          }}
                        >
                          Modules
                        </Button>
                        <Button
                          flex={1}
                          size="sm"
                          colorScheme={u.status === "Active" ? "red" : "green"}
                          onClick={async () => {
                            const confirmMsg =
                              u.status === "Active"
                                ? "Deactivate this user?"
                                : "Activate this user?";
                            if (!window.confirm(confirmMsg)) return;
                            try {
                              await rbacService.updateUserStatus(u._id, {
                                isActive: u.status !== "Active",
                              });
                              toast({
                                title: `User ${
                                  u.status === "Active"
                                    ? "deactivated"
                                    : "activated"
                                }`,
                                status: "success",
                              });
                              load();
                            } catch (e) {
                              console.error(e);
                              toast({
                                title:
                                  e?.response?.data?.message ||
                                  "Status update failed",
                                status: "error",
                              });
                            }
                          }}
                        >
                          {u.status === "Active" ? "Deactivate" : "Activate"}
                        </Button>
                      </HStack>
                    </Stack>
                  </Flex>
                </Box>
              );
            })}
          </VStack>
        ) : (
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Name</Th>
                <Th>Email</Th>
                <Th>Employee ID</Th>
                <Th>Role</Th>
                <Th>Status</Th>
                <Th>Updated</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filtered.map((u) => {
                const primary = u.role || (u.roles && u.roles[0]) || "";
                return (
                  <Tr key={u._id}>
                    <Td>{u.name || u.email}</Td>
                    <Td>{u.email}</Td>
                    <Td>{u.employeeId || "—"}</Td>
                    <Td>
                      <HStack spacing={1}>
                        {(u.roles || (primary ? [primary] : [])).map((r) => {
                          const rd = roles[r] || {};
                          return (
                            <Badge key={r} colorScheme={rd.color || "gray"}>
                              {rd.name || r}
                            </Badge>
                          );
                        })}
                      </HStack>
                    </Td>
                    <Td>{u.status || "Active"}</Td>
                    <Td>
                      <HStack spacing={1}>
                        {(u.allowed_modules || []).map((m) => (
                          <Badge key={m} colorScheme="gray">
                            {m}
                          </Badge>
                        ))}
                      </HStack>
                    </Td>
                    <Td>
                      {new Date(u.updatedAt || Date.now()).toLocaleDateString()}
                    </Td>
                    <Td>
                      <VStack align="stretch" spacing={2}>
                        <Select
                          size="sm"
                          value={primary}
                          onChange={(e) => updateRole(u, e.target.value)}
                        >
                          <option value="">(none)</option>
                          {Object.keys(roles).map((k) => (
                            <option key={k} value={k}>
                              {roles[k].name || k}
                            </option>
                          ))}
                        </Select>
                        <HStack>
                          <Button size="xs" onClick={() => openConfirm(u)}>
                            Confirm Change
                          </Button>
                          <Button
                            size="xs"
                            onClick={() => {
                              setSelectedUser(u);
                              setSelectedModules(u.allowed_modules || []);
                              onModulesOpen();
                            }}
                          >
                            Modules
                          </Button>
                          <Button
                            size="xs"
                            colorScheme={
                              u.status === "Active" ? "red" : "green"
                            }
                            onClick={async () => {
                              const confirmMsg =
                                u.status === "Active"
                                  ? "Deactivate this user?"
                                  : "Activate this user?";
                              if (!window.confirm(confirmMsg)) return;
                              try {
                                await rbacService.updateUserStatus(u._id, {
                                  isActive: u.status !== "Active",
                                });
                                toast({
                                  title: `User ${
                                    u.status === "Active"
                                      ? "deactivated"
                                      : "activated"
                                  }`,
                                  status: "success",
                                });
                                load();
                              } catch (e) {
                                console.error(e);
                                toast({
                                  title:
                                    e?.response?.data?.message ||
                                    "Status update failed",
                                  status: "error",
                                });
                              }
                            }}
                          >
                            {u.status === "Active" ? "Deactivate" : "Activate"}
                          </Button>
                        </HStack>
                      </VStack>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        )}
      </Box>

      {/* Modules modal */}
      <Modal
        isOpen={isModulesOpen}
        onClose={() => {
          setSelectedUser(null);
          setSelectedModules([]);
          onModulesClose();
        }}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Manage Modules</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={2}>
              User: {selectedUser?.email || selectedUser?.name}
            </Text>
            <Stack spacing={2}>
              <Checkbox
                isChecked={
                  selectedModules.length === moduleOptions.length &&
                  moduleOptions.length > 0
                }
                onChange={(e) => {
                  if (e.target.checked) setSelectedModules([...moduleOptions]);
                  else setSelectedModules([]);
                }}
              >
                All Modules
              </Checkbox>
              {moduleOptions.map((m) => (
                <Checkbox
                  key={m}
                  isChecked={selectedModules.includes(m)}
                  onChange={(e) => {
                    if (e.target.checked)
                      setSelectedModules((s) => Array.from(new Set([...s, m])));
                    else setSelectedModules((s) => s.filter((x) => x !== m));
                  }}
                >
                  {m}
                </Checkbox>
              ))}
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button
              mr={3}
              onClick={() => {
                setSelectedUser(null);
                setSelectedModules([]);
                onModulesClose();
              }}
            >
              Cancel
            </Button>
            <Button
              colorScheme="teal"
              onClick={async () => {
                if (!selectedUser) return;
                try {
                  // if All selected, ensure full list
                  const payload = { modules: selectedModules };
                  await rbacService.updateUserModules(
                    selectedUser._id,
                    payload
                  );
                  toast({ title: "Modules updated", status: "success" });
                  setSelectedUser(null);
                  setSelectedModules([]);
                  onModulesClose();
                  load();
                } catch (e) {
                  console.error(e);
                  toast({
                    title: e?.response?.data?.message || "Update failed",
                    status: "error",
                  });
                }
              }}
            >
              Update Modules
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Box mt={4}>
        <Heading size="sm" mb={2}>
          Role Definitions
        </Heading>
        <HStack spacing={3} wrap="wrap">
          {Object.keys(roles).map((k) => (
            <Box key={k} p={2} borderWidth={1} borderRadius="md">
              <Text fontWeight="bold">{roles[k].name || k}</Text>
              <Text fontSize="sm">{roles[k].description}</Text>
              <Text fontSize="xs" mt={1}>
                {(roles[k].permissions || []).join(", ")}
              </Text>
            </Box>
          ))}
        </HStack>
      </Box>

      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Confirm role change
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to change role for{" "}
              <b>{selectedUser?.name || selectedUser?.email}</b>?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose} mr={3}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={async () => {
                  if (!selectedUser) return onClose();
                  try {
                    const s = users.find((x) => x._id === selectedUser._id);
                    if (s) {
                      const primary = s.role || (s.roles && s.roles[0]) || "";
                      await updateRole(s, primary);
                    }
                  } catch (e) {
                    console.error(e);
                  } finally {
                    setSelectedUser(null);
                    onClose();
                  }
                }}
              >
                Yes, change
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default RBAC;
